import { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { ProcessedImage, ConfirmedEntry } from '@/components/ai-entry/types';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { autoSaveAreaToCity, resolveCityFromAddress, resolveCityFromArea } from '@/lib/countryStore';

type Phase = 'upload' | 'preprocess' | 'processing' | 'review' | 'results';

interface AIEntryContextType {
  phase: Phase;
  images: ProcessedImage[];
  progress: number;
  reviewIndex: number;
  confirmed: ConfirmedEntry[];
  rawImages: { fileName: string; base64: string }[];
  isStillProcessing: boolean;
  setPhase: (p: Phase) => void;
  setImages: (imgs: ProcessedImage[]) => void;
  setProgress: (p: number) => void;
  setReviewIndex: (i: number) => void;
  setConfirmed: React.Dispatch<React.SetStateAction<ConfirmedEntry[]>>;
  setRawImages: (imgs: { fileName: string; base64: string }[]) => void;
  handleImagesUploaded: (imgs: { fileName: string; base64: string }[]) => void;
  handleFileUploaded: (file: { type: 'excel' | 'pdf'; content: string; fileName: string }) => void;
  handleTextPasted: (text: string) => void;
  handlePreprocessDone: (imgs: { fileName: string; base64: string }[]) => void;
  handlePreprocessCancel: () => void;
  handleConfirm: (entry: ConfirmedEntry) => void;
  handleConfirmAll: (correctionResolver?: (correction: import('@/lib/countryStore').AreaCorrectionResult) => Promise<'accept' | 'skip'>) => Promise<void>;
  handleSkip: () => void;
  handleNewSession: () => void;
  successImages: ProcessedImage[];
}

const AIEntryContext = createContext<AIEntryContextType | null>(null);

export const useAIEntry = () => {
  const ctx = useContext(AIEntryContext);
  if (!ctx) throw new Error('useAIEntry must be used within AIEntryProvider');
  return ctx;
};

export const AIEntryProvider = ({ children }: { children: ReactNode }) => {
  const [phase, setPhaseState] = useState<Phase>('upload');
  const [images, setImagesState] = useState<ProcessedImage[]>([]);
  const [progress, setProgress] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [confirmed, setConfirmed] = useState<ConfirmedEntry[]>([]);
  const [rawImages, setRawImages] = useState<{ fileName: string; base64: string }[]>([]);
  const [isStillProcessing, setIsStillProcessing] = useState(false);

  const reviewQueueRef = useRef<string[]>([]);
  const [reviewQueue, setReviewQueue] = useState<string[]>([]);

  const phaseRef = useRef<Phase>('upload');
  const imagesRef = useRef<ProcessedImage[]>([]);
  const reviewIndexRef = useRef(0);

  const setPhase = useCallback((p: Phase) => { phaseRef.current = p; setPhaseState(p); }, []);
  const setImages = useCallback((imgs: ProcessedImage[]) => { imagesRef.current = imgs; setImagesState(imgs); }, []);
  const setReviewIndexWrapped = useCallback((i: number) => { reviewIndexRef.current = i; setReviewIndex(i); }, []);

  const handleImagesUploaded = useCallback((imgs: { fileName: string; base64: string }[]) => {
    setRawImages(imgs);
    setPhase('preprocess');
  }, [setPhase]);

  // Handle Excel/PDF file upload
  const handleFileUploaded = useCallback(async (file: { type: 'excel' | 'pdf'; content: string; fileName: string }) => {
    setPhase('processing');
    setProgress(0);
    setIsStillProcessing(false);
    reviewQueueRef.current = [];
    setReviewQueue([]);

    try {
      setProgress(30);
      const { data, error } = await supabase.functions.invoke('extract-shipments-file', {
        body: { type: file.type, content: file.content, fileName: file.fileName },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const shipments = data?.shipments || [];
      if (shipments.length === 0) {
        toast.error('لم يتم العثور على شحنات في الملف');
        setPhase('upload');
        return;
      }

      setProgress(80);

      // Create ProcessedImage entries for each shipment (no image)
      const items: ProcessedImage[] = shipments.map((s: any, i: number) => ({
        id: `file-${Date.now()}-${i}`,
        fileName: `${file.fileName} - #${i + 1}`,
        imageBase64: '',
        data: {
          barcode: s.barcode || '',
          receiverName: s.receiverName || '',
          city: s.city || '',
          address: s.address || '',
          addressSuggestion: '',
          phone: s.phone || '',
          phone2: s.phone2 || '',
          category: '',
          price: s.price || '',
          notes: s.notes || '',
        },
        status: 'success' as const,
      }));

      setImages(items);
      setProgress(100);

      // Build review queue
      const queue = items.map(it => it.id);
      reviewQueueRef.current = queue;
      setReviewQueue(queue);
      setReviewIndexWrapped(0);
      setPhase('review');

      toast.success(`تم استخراج ${shipments.length} شحنة من الملف`);
    } catch (err: any) {
      console.error('File extraction error:', err);
      toast.error(err.message || 'فشل استخراج البيانات من الملف');
      setPhase('upload');
    }
  }, [setPhase, setImages, setReviewIndexWrapped]);

  const handleTextPasted = useCallback(async (text: string) => {
    setPhase('processing');
    setProgress(0);
    setIsStillProcessing(false);
    reviewQueueRef.current = [];
    setReviewQueue([]);

    try {
      setProgress(30);
      const { data, error } = await supabase.functions.invoke('extract-shipments-file', {
        body: { type: 'text', content: text, fileName: 'pasted-text' },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const shipments = data?.shipments || [];
      if (shipments.length === 0) {
        toast.error('لم يتم العثور على شحنات في النص');
        setPhase('upload');
        return;
      }

      setProgress(80);

      const items: ProcessedImage[] = shipments.map((s: any, i: number) => ({
        id: `text-${Date.now()}-${i}`,
        fileName: `نص ملصوق - #${i + 1}`,
        imageBase64: '',
        data: {
          barcode: s.barcode || '',
          receiverName: s.receiverName || '',
          city: s.city || '',
          address: s.address || '',
          addressSuggestion: '',
          phone: s.phone || '',
          phone2: s.phone2 || '',
          category: '',
          price: s.price || '',
          notes: s.notes || '',
        },
        status: 'success' as const,
      }));

      setImages(items);
      setProgress(100);

      const queue = items.map(it => it.id);
      reviewQueueRef.current = queue;
      setReviewQueue(queue);
      setReviewIndexWrapped(0);
      setPhase('review');

      toast.success(`تم استخراج ${shipments.length} شحنة من النص`);
    } catch (err: any) {
      console.error('Text extraction error:', err);
      toast.error(err.message || 'فشل استخراج البيانات من النص');
      setPhase('upload');
    }
  }, [setPhase, setImages, setReviewIndexWrapped]);

  const processImages = useCallback(async (imgs: { fileName: string; base64: string }[]) => {
    const items: ProcessedImage[] = imgs.map((img, i) => ({
      id: `img-${Date.now()}-${i}`,
      fileName: img.fileName,
      imageBase64: img.base64,
      data: null,
      status: 'pending' as const,
    }));
    setImages(items);
    setPhase('processing');
    setProgress(0);
    setIsStillProcessing(true);
    reviewQueueRef.current = [];
    setReviewQueue([]);

    const results = [...items];
    let completed = 0;
    let firstSuccessSent = false;
    const CONCURRENCY = 3;

    const processSingle = async (index: number) => {
      results[index].status = 'processing';
      setImages([...results]);

      let attempts = 0;
      let success = false;
      while (attempts < 3 && !success) {
        try {
          const { data, error } = await supabase.functions.invoke('extract-waybill', {
            body: {
              imageBase64: results[index].imageBase64,
              fileName: results[index].fileName,
            },
          });
          if (error) throw error;
          if (data?.error) throw new Error(data.error);
          results[index].data = data;
          results[index].status = 'success';
          success = true;
        } catch (err: any) {
          attempts++;
          if (attempts >= 3) {
            results[index].status = 'error';
            results[index].error = err.message || 'Unknown error';
          } else {
            await new Promise(r => setTimeout(r, 1000 * attempts));
          }
        }
      }
      completed++;
      setProgress(Math.round((completed / results.length) * 100));
      setImages([...results]);

      if (success) {
        const imgId = results[index].id;
        if (!reviewQueueRef.current.includes(imgId)) {
          reviewQueueRef.current = [...reviewQueueRef.current, imgId];
          setReviewQueue([...reviewQueueRef.current]);
        }
      }

      if (success && !firstSuccessSent) {
        firstSuccessSent = true;
        if (phaseRef.current === 'processing') {
          setReviewIndexWrapped(0);
          setPhase('review');
        }
      }
    };

    for (let i = 0; i < results.length; i += CONCURRENCY) {
      const batch = results.slice(i, i + CONCURRENCY).map((_, j) => processSingle(i + j));
      await Promise.all(batch);
    }

    setIsStillProcessing(false);

    const successItems = results.filter(r => r.status === 'success');
    if (successItems.length === 0) {
      toast.error('فشل استخراج جميع الصور');
      setPhase('upload');
    }
  }, [setImages, setPhase, setReviewIndexWrapped]);

  const handlePreprocessDone = useCallback((imgs: { fileName: string; base64: string }[]) => {
    setRawImages([]);
    processImages(imgs);
  }, [processImages]);

  const handlePreprocessCancel = useCallback(() => {
    setRawImages([]);
    setPhase('upload');
  }, [setPhase]);

  const successImages = reviewQueue
    .map(id => images.find(img => img.id === id))
    .filter((img): img is ProcessedImage => !!img);

  const handleConfirm = useCallback((entry: ConfirmedEntry) => {
    setConfirmed(prev => [...prev, entry]);
    const nextIdx = reviewIndexRef.current + 1;
    if (nextIdx < reviewQueueRef.current.length) {
      setReviewIndexWrapped(nextIdx);
    } else if (isStillProcessing) {
      // More images still processing
    } else {
      setPhase('results');
    }
  }, [isStillProcessing, setPhase, setReviewIndexWrapped]);

  const handleConfirmAll = useCallback(async (correctionResolver?: (correction: import('@/lib/countryStore').AreaCorrectionResult) => Promise<'accept' | 'skip'>) => {
    const currentImages = imagesRef.current;
    const queue = reviewQueueRef.current;
    const startIdx = reviewIndexRef.current;
    const newEntries: ConfirmedEntry[] = [];
    for (let i = startIdx; i < queue.length; i++) {
      const img = currentImages.find(im => im.id === queue[i]);
      if (img && img.data) {
        const entryData = { ...img.data };
        
        // Try to resolve city from area name first
        if (entryData.city?.trim()) {
          const areaResolved = resolveCityFromArea(entryData.city);
          if (areaResolved) {
            const oldCity = entryData.city;
            entryData.city = areaResolved.cityNameAr;
            entryData.address = oldCity + (entryData.address ? ' - ' + entryData.address : '');
          }
        }
        
        // If city is still empty, try to guess from address
        if (!entryData.city?.trim() && entryData.address?.trim()) {
          const fromAddress = resolveCityFromAddress(entryData.address);
          if (fromAddress) {
            entryData.city = fromAddress.cityNameAr;
            if (fromAddress.matchedArea === '__default__') {
              toast.warning('لم يتم العثور على منطقة مطابقة، تم تعيين عمان كمدينة افتراضية', { duration: 6000 });
            }
          } else {
            // Absolute fallback — set Amman
            entryData.city = 'عمان';
            toast.warning('لم يتم العثور على منطقة مطابقة، تم تعيين عمان كمدينة افتراضية', { duration: 6000 });
          }
        }
        
        // Cross-city check — show dialog for confirmation if resolver provided
        if (entryData.city?.trim() && entryData.address?.trim()) {
          const correction = autoSaveAreaToCity(entryData.city, entryData.address);
          if (correction?.corrected && correction.correctedCity && correctionResolver) {
            const decision = await correctionResolver(correction);
            if (decision === 'accept') {
              entryData.city = correction.correctedCity!;
            }
          }
        }
        newEntries.push({ ...entryData, id: img.id, fileName: img.fileName });
      }
    }
    if (newEntries.length > 0) {
      setConfirmed(prev => [...prev, ...newEntries]);
      setPhase('results');
    } else {
      toast.info('لا توجد عناصر للإضافة');
    }
  }, [setPhase]);

  const handleSkip = useCallback(() => {
    const nextIdx = reviewIndexRef.current + 1;
    if (nextIdx < reviewQueueRef.current.length) {
      setReviewIndexWrapped(nextIdx);
    } else if (isStillProcessing) {
      // Wait for more
    } else {
      setConfirmed(prev => {
        if (prev.length > 0) {
          setPhase('results');
        } else {
          toast.info('لا توجد عناصر مؤكدة');
          setPhase('upload');
        }
        return prev;
      });
    }
  }, [isStillProcessing, setPhase, setReviewIndexWrapped]);

  const handleNewSession = useCallback(() => {
    setPhase('upload');
    setImages([]);
    setConfirmed([]);
    setReviewIndexWrapped(0);
    setProgress(0);
    setIsStillProcessing(false);
    reviewQueueRef.current = [];
    setReviewQueue([]);
  }, [setPhase, setImages, setReviewIndexWrapped]);

  return (
    <AIEntryContext.Provider value={{
      phase, setPhase, images, setImages, progress, setProgress,
      reviewIndex, setReviewIndex: setReviewIndexWrapped, confirmed, setConfirmed,
      rawImages, setRawImages, isStillProcessing,
      handleImagesUploaded, handleFileUploaded, handleTextPasted, handlePreprocessDone, handlePreprocessCancel,
      handleConfirm, handleConfirmAll, handleSkip, handleNewSession, successImages,
    }}>
      {children}
    </AIEntryContext.Provider>
  );
};
